import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const NEWSPAPER = "/assets/newspaper.png";

export default function Hero({ ready = true }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Parallax the newspaper as user scrolls
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-onyx"
    >
      {/* Newspaper image reveals via preloader zoom.
          Once preloader.done → ready=true → image gently scales into place. */}
      <motion.div
        initial={{ scale: 0.05, opacity: 0 }}
        animate={
          ready
            ? { scale: 1, opacity: 1, transition: { duration: 1.4, ease: [0.16, 1, 0.3, 1] } }
            : { scale: 0.05, opacity: 0 }
        }
        className="absolute inset-0"
      >
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img
            src={NEWSPAPER}
            alt="The studio's editorial spread — ऋ Design"
            className="w-full h-full object-cover"
            draggable={false}
          />
          {/* Deep vignette + brass wash to blend the sepia into brand palette */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(15,17,14,0.35) 0%, rgba(15,17,14,0.15) 25%, rgba(15,17,14,0.55) 65%, rgba(15,17,14,0.96) 100%)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(15,17,14,0.55) 0%, rgba(15,17,14,0.15) 32%, rgba(15,17,14,0.05) 60%, rgba(15,17,14,0.35) 100%)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 70% 55% at 50% 45%, transparent 30%, rgba(15,17,14,0.35) 100%)",
              mixBlendMode: "multiply",
            }}
          />
        </motion.div>
      </motion.div>

      {/* Corner overlines */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={ready ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 1.4, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-24 md:top-28 left-6 md:left-10 z-10 flex items-center gap-3 text-cream/75"
      >
        <span className="w-6 h-px bg-brass" />
        <span className="text-[10px] md:text-[11px] tracking-overline uppercase font-medium text-brass">
          Vol. 01 · Issue 01 · The Studio Reads
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={ready ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute top-24 md:top-28 right-6 md:right-10 z-10 text-right text-[10px] md:text-[11px] tracking-overline uppercase text-cream/70"
      >
        New Delhi<br />
        <span className="text-brass">Since 2020</span>
      </motion.div>

      {/* Main tagline block — positioned upper-left over the neutral face area, subtitle-sized */}
      <motion.div
        style={{ y: titleY, opacity: titleOpacity }}
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-10 px-6 md:px-10"
      >
        <div className="max-w-[1440px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={ready ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 1.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-lg"
          >
            <h1
              className="font-serif font-light tracking-editorial leading-[0.95] text-cream"
              style={{
                fontSize: "clamp(48px, 5vw, 88px)",
                textShadow: "0 8px 60px rgba(15,17,14,0.8), 0 0 40px rgba(15,17,14,0.6)",
              }}
            >
              A studio<span className="text-brass">,</span> <br />
              <span className="italic">personified.</span>
            </h1>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom band — subtitle + scroll cue on a solid dark bar */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={ready ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 1.85, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-x-0 bottom-0 z-10 px-6 md:px-10 pb-8 md:pb-10"
      >
        <div className="max-w-[1440px] mx-auto flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <p className="max-w-md text-base md:text-lg text-cream/85 leading-relaxed font-light"
             style={{ textShadow: "0 4px 30px rgba(15,17,14,0.9)" }}>
            One editor's eye across interiors, styling, brand and the small
            ceremonies in between. A design concierge for the discerning few.
          </p>
          <div className="flex items-center gap-3 text-[10px] tracking-overline uppercase text-cream/80"
               style={{ textShadow: "0 2px 12px rgba(15,17,14,0.9)" }}>
            <span>Scroll</span>
            <span className="w-8 h-px bg-brass/80" />
            <span>Read on</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
